import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useMultiTabLogout } from '../../../../../common/hooks/useMultiTabLogout';
import Icons from '../../../../../common/widgets/Icons';
import { MenuListItem } from '../../../../../common/widgets/MenuListItem';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { resources } from '../../../resources/resources';

interface IUserMenuProps {
  readonly menuClose?: () => void;
}

export default function UserMenu(props: IUserMenuProps) {
  const { t: translate } = useTranslation();
  const { triggerLogout } = useMultiTabLogout();
  const { menuClose } = props;
  const { customerInfo } = useContext(CustomerInfoContext);

  const auth = useAuth();
  const navigate = useNavigate();

  const handleClick = (linkPath: string) => {
    return () => {
      navigate(linkPath);
      if (menuClose) menuClose();
    };
  };

  const handleLogout = async () => {
    triggerLogout();
    await auth.signoutRedirect();
  };

  return (
    <div className="action-panel account-nav-panel">
      <div className="action-panel-content account-nav">
        <ul>
          {!customerInfo.isSavingsUser && (
            <MenuListItem
              text={translate(resources.menu.dashboard)}
              icon={<Icons.AccountMenuDashboard />}
              customOnClick={handleClick('/my-account/dashboard')}
              linkPath="/my-account/dashboard"
            />
          )}
          {(customerInfo.canAccessMyBenefits ||
            customerInfo.canAccessExclusive ||
            customerInfo.canAccessSubscriptions) && (
            <MenuListItem
              text={translate(resources.menu.myBenefits)}
              icon={<Icons.AccountMenuSubscriptions />}
              customOnClick={handleClick('/my-account/my-benefits')}
              linkPath="/my-account/my-benefits"
            />
          )}
          {!customerInfo.isSavingsUser &&
            customerInfo.canAccessTimeAndAttendanceModule && (
              <MenuListItem
                text={translate(resources.menu.attendance)}
                icon={<Icons.AccountMenuAttendance />}
                customOnClick={handleClick('/my-account/time-and-attendance')}
                linkPath="/my-account/time-and-attendance"
              />
            )}
          <MenuListItem
            text={translate(resources.menu.orders)}
            icon={<Icons.AccountMenuOrders />}
            customOnClick={handleClick('/my-account/orders')}
            linkPath="/my-account/orders"
          />
          {customerInfo.canAccessSubscriptions && (
            <MenuListItem
              text={translate(resources.menu.subscriptions)}
              icon={<Icons.AccountMenuSubscriptions />}
              customOnClick={handleClick('/my-account/subscriptions')}
              linkPath="/my-account/subscriptions"
            />
          )}
          {!customerInfo.isSavingsUser && (
            <MenuListItem
              text={translate(resources.menu.notifications)}
              icon={<Icons.AccountMenuNotifications />}
              customOnClick={handleClick('/my-account/notifications')}
              linkPath="/my-account/notifications"
              additionalElement={
                customerInfo.notificationsCount > 0 && (
                  <span className="alerts-count-menu">
                    <span>{customerInfo.notificationsCount}</span>
                  </span>
                )
              }
            />
          )}
          {!customerInfo.isSavingsUser && (
            <MenuListItem
              text={translate(resources.menu.favorite)}
              icon={<Icons.AccountMenuFavorite />}
              customOnClick={handleClick('/my-account/wishlist')}
              linkPath="/my-account/wishlist"
            />
          )}
          <MenuListItem
            text={translate(resources.menu.vouchers)}
            icon={<Icons.AccountMenuVouchers />}
            customOnClick={handleClick('/my-account/vouchers')}
            linkPath="/my-account/vouchers"
          />
          {!customerInfo.isSavingsUser && customerInfo.canAccessMyCards && (
            <MenuListItem
              text={translate(resources.cards)}
              icon={<Icons.AccountMenuCards />}
              customOnClick={handleClick('/my-account/cards')}
              linkPath="/my-account/cards"
            />
          )}
          <MenuListItem
            text={translate(resources.menu.preferences)}
            icon={<Icons.AccountMenuPreferences />}
            customOnClick={handleClick('/my-account/preferences')}
            linkPath="/my-account/preferences"
          />
          <MenuListItem
            text={translate(resources.menu.accountInfo)}
            icon={<Icons.AccountMenuInfo />}
            customOnClick={handleClick('/my-account/account-info')}
            linkPath="/my-account/account-info"
          />
          {!customerInfo.isSavingsUser && (
            <MenuListItem
              text={translate(resources.menu.passwordChange)}
              icon={<Icons.AccountMenuSecurity />}
              customOnClick={handleClick('/my-account/change-password')}
              linkPath="/my-account/change-password"
            />
          )}
          {!customerInfo.isSavingsUser &&
            customerInfo.canGenerateDemoAccount && (
              <MenuListItem
                text={translate(resources.demoAccount.title)}
                icon={<Icons.AccountMenuDemoAccount />}
                customOnClick={handleClick('/my-account/create-demo-account')}
                linkPath="/my-account/create-demo-account"
              />
            )}
          {!customerInfo.isSavingsUser && (
            <MenuListItem
              text={translate(resources.menu.logout)}
              icon={<Icons.AccountMenuLogout />}
              customOnClick={handleLogout}
            />
          )}
        </ul>
      </div>
    </div>
  );
}
